import sanitizeHtml from 'sanitize-html';

const emojiUnicode = '\u00a9|\u00ae|\u2000-\u3300|\ud83c\ud000-\udfff|\ud83d\ud000-\udfff|\ud83e\ud000-\udfff';
const emptyCharacters = '\\s,.\\/\\\\<>?!@#$%^&*()_+=`:;\\-';
const prefixGroup = [emojiUnicode, emptyCharacters];
export const latinSeparatorReg = new RegExp(`[${prefixGroup.join('')}]+`, 'g');
export const latinEmptyCharacterEndingReg = new RegExp(`[${emptyCharacters}]$`);

const hansEmptyCharacters = '。？！，、；：「」『』‘’“”（）〔〕【｜】—… ·《》〈〉';
export const thaiSubscriptReg = new RegExp('[\u0E31|\u0E33-\u0E3A|\u0E47-\u0E4E]');
export const hansAndThaiSeparatorReg = new RegExp(`[${[hansEmptyCharacters, emptyCharacters].join('')}]+`);

export function keyWordHighlight(content = '', keywords: string[] = [], needTokenize = true): string {
  if (keywords?.length <= 0) {
    return content;
  }
  const keywordsWithSort = keywords.sort((a, b) => b.length - a.length);

  if (!needTokenize) {
    return sanitizeHtml(handleNoNeedTokenize(content, keywordsWithSort)).trim();
  }

  const [keywordNotNeedTokenize, keywordNeedTokenize] = divergeKeywords(keywordsWithSort);

  content = handleNoNeedTokenize(content, keywordNotNeedTokenize);
  content = handleNeedTokenize(content, keywordNeedTokenize);

  return sanitizeHtml(content).trim();
}

export function getNeedTokenizeReg(keywords: string[]): RegExp {
  const prefix = `[${prefixGroup.join('')}]`;
  // 由于 ios 对后行零宽断言的兼容性不佳, 不使用零宽断言, 通过在匹配文字开头加一个空格使其满足正则规则
  return new RegExp(`(^|${prefix})(${keywords.map(escapeRegex).join('|')})(?=$|${prefix})`, 'ig');
}

export function getNoNeedTokenizeReg(keywords: string[]): RegExp {
  return new RegExp(keywords.map(escapeRegex).join('|'), 'ig');
}

export const checkIfNeedTokenize = (keyword: string) => !new RegExp(`[${hansUnicode}${thaiUnicode}]`).test(keyword);

// 后台返回的token有可能是需要转义的字符，所以增加转义字符串的方法
function escapeRegex(str: string) {
  return str.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
}

// 拉丁文场景, 根据分隔符如空格等进行分词, 只匹配单词
function handleNeedTokenize(content: string, keywords: string[]) {
  if (!keywords.length) {
    return content;
  }

  return content.replace(getNeedTokenizeReg(keywords), (match, ...rest) => {
    if (!match) {
      return match;
    }
    return `${rest[0]}<em>${rest[1]}</em>`;
  });
}

// 泰文和中文不分词, 直接替换(即使不是完整的单词)
// 2021.08.17：泰文中带有韵母调类符号，母音附标的文字需要分词统一处理，不能分开会导致iOS端显示异常
function handleNoNeedTokenize(content: string, keywords: string[]) {
  if (!keywords.length) {
    return content;
  }

  return content.replace(getNoNeedTokenizeReg(keywords), (match, offset) => {
    if (!match || thaiSubscriptReg.test(content[offset + match.length])) {
      return match;
    }

    return `<em>${match}</em>`;
  });
}

const thaiUnicode = '\u0E00-\u0E7F';
const hansUnicode = '\u4E00-\u9fD5';

function divergeKeywords(keywords: string[]) {
  const reg = new RegExp(`[${hansUnicode}${thaiUnicode}]`);

  return keywords.reduce<[string[], string[]]>(
    (acc, cur) => {
      if (reg.test(cur)) {
        acc[0].push(cur);
      } else {
        acc[1].push(cur);
      }
      return acc;
    },
    [[], []],
  );
}
